import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RuledPage } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * NotFound (/*) — renders when the router hits a path it doesn't
 * recognise (see router.tsx:296 catch-all). Fixed in Kong PR #1 from
 * silently redirecting to /; illustrated + contextual in Kong PR #22.
 *
 * Design spec: docs/design/auth/not-found.md (Option A + Option C).
 * - Bespoke torn-register-page SVG as the identity visual
 * - Search input pointing at Google site: search (real internal search
 *   isn't shipped)
 * - Three contextual "back to" tiles, tailored by pathname prefix
 *   (student / TPO / group / partner / etc — see suggestForPath)
 */

interface Destination {
  label: string;
  to: string;
}

const DEFAULT_DESTS: Destination[] = [
  { label: 'Homepage',        to: '/' },
  { label: 'For students',    to: '/for-students' },
  { label: 'Book a demo',     to: '/contact?topic=demo' },
];

/**
 * Contextual "back to" list. Same trick as the /error page — the
 * pathname prefix tells us which shell the user was aiming for.
 */
function suggestForPath(pathname: string): Destination[] {
  if (pathname.startsWith('/tpo/'))          return [{ label: 'TPO today', to: '/tpo/today' },     { label: 'All drives', to: '/tpo/drives' },       { label: 'Book a demo', to: '/contact?topic=demo' }];
  if (pathname.startsWith('/group/'))        return [{ label: 'Group overview', to: '/group/overview' }, { label: 'Colleges', to: '/group/colleges' }, { label: 'Book a demo', to: '/contact?topic=demo' }];
  if (pathname.startsWith('/company/'))      return [{ label: 'Openings',     to: '/company/openings' },  { label: 'Candidates',  to: '/company/candidates' }, { label: 'Post an opportunity', to: '/register/partner' }];
  if (pathname.startsWith('/platform/'))     return [{ label: 'Dashboard',    to: '/platform/dashboard' }, { label: 'Tenants',    to: '/platform/tenants' },   { label: 'Audit log',     to: '/platform/audit' }];
  if (pathname.startsWith('/student/') ||
      pathname === '/home' ||
      pathname.startsWith('/opportunities/') ||
      pathname.startsWith('/applications')) return [{ label: 'Home',         to: '/home' },              { label: 'Applications', to: '/applications' },     { label: 'For students', to: '/for-students' }];
  if (pathname.startsWith('/for-'))          return [{ label: 'Homepage',     to: '/' },                  { label: 'Pricing',      to: '/pricing' },          { label: 'Book a demo',  to: '/contact?topic=demo' }];
  if (pathname.startsWith('/register') ||
      pathname.startsWith('/login') ||
      pathname.startsWith('/accept-invite') ||
      pathname.startsWith('/verify-email')) return [{ label: 'Sign in',      to: '/login' },             { label: 'Create account', to: '/register' },        { label: 'Homepage',       to: '/' }];
  return DEFAULT_DESTS;
}

export function NotFoundPage() {
  const loc = useLocation();
  const dests = useMemo(() => suggestForPath(loc.pathname), [loc.pathname]);
  const searchQuery = loc.pathname.replace(/[^a-zA-Z0-9 ]+/g, ' ').trim();

  return (
    <RuledPage>
      <PageMeta title="Not found" canonical={loc.pathname} noindex />
      <section className="max-w-page-narrow mx-auto px-6 py-16 lg:py-24 text-center grid gap-8">
        {/* Illustration */}
        <TornRegisterPage />

        <div>
          <p className="eyebrow">404</p>
          <h1 className="font-serif text-4xl lg:text-5xl mt-2">
            This page isn't in the register.
          </h1>
          <p className="mt-3 text-ink-secondary max-w-md mx-auto">
            The page at{' '}
            <code
              className="px-1.5 py-0.5 rounded"
              style={{
                background: 'rgb(var(--bg-tertiary))',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.9em',
              }}
            >
              {loc.pathname}
            </code>{' '}
            doesn't exist — or hasn't been written yet. Here's where you might have wanted to go.
          </p>
        </div>

        {/* Search — deep-links to Google site: search since internal search isn't shipped */}
        <form
          className="max-w-md mx-auto w-full"
          action="https://www.google.com/search"
          method="get"
          target="_blank"
          rel="noreferrer noopener"
        >
          <input type="hidden" name="q" value={`site:kollez.ai ${searchQuery}`.trim()} />
          <label className="grid gap-2 text-left">
            <span className="label">Search Kollez</span>
            <input
              className="input"
              name="q"
              type="search"
              placeholder="e.g. pricing, TPO, DPDP…"
              defaultValue={searchQuery}
            />
          </label>
        </form>

        {/* Contextual destinations */}
        <div>
          <p className="micro" style={{ color: 'rgb(var(--ink-tertiary))' }}>
            Popular destinations
          </p>
          <div className="mt-3 flex flex-wrap gap-2 justify-center">
            {dests.map((d) => (
              <Link key={d.to} to={d.to} className="btn btn-outline btn-sm">
                {d.label}
              </Link>
            ))}
          </div>
        </div>

        <p className="helper">
          If a link brought you here that should have worked, please{' '}
          <a
            className="link"
            href={`mailto:support@kollez.ai?subject=Broken%20link&body=${encodeURIComponent(
              `Path: ${loc.pathname}\nReferrer: ${document.referrer || 'unknown'}`,
            )}`}
          >
            report it
          </a>
          .
        </p>
      </section>
    </RuledPage>
  );
}

/**
 * Bespoke SVG — a register page with the corner torn off, red-margin
 * rule visible, a single ink blot. Kollez's identity metaphor applied
 * to a 404 moment. Both themes: paper background is a token
 * (--bg-primary), everything else is theme-aware.
 */
function TornRegisterPage() {
  return (
    <svg
      viewBox="0 0 320 220"
      width="240"
      height="165"
      role="img"
      aria-label="A register page with a torn corner"
      style={{ margin: '0 auto', maxWidth: '100%', height: 'auto' }}
    >
      {/* Paper card body — top-right corner torn */}
      <path
        d="M 20 20 L 240 20 L 300 80 L 300 200 L 20 200 Z"
        fill="rgb(var(--bg-primary))"
        stroke="rgb(var(--line-primary))"
        strokeWidth="1.5"
      />
      {/* Torn edge — subtle inner fold shadow */}
      <path
        d="M 240 20 L 300 80 L 240 80 Z"
        fill="rgb(var(--bg-tertiary))"
        stroke="rgb(var(--line-primary))"
        strokeWidth="1.5"
      />
      {/* Red margin rule (register signature) */}
      <line
        x1="46"
        y1="20"
        x2="46"
        y2="200"
        stroke="rgb(var(--margin))"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {/* Ruled lines — 6 evenly spaced, faded */}
      {[60, 88, 116, 144, 172].map((y) => (
        <line
          key={y}
          x1="60"
          y1={y}
          x2="285"
          y2={y}
          stroke="rgb(var(--rule))"
          strokeWidth="1"
          opacity={y > 88 ? 0.7 : 1}
        />
      ))}
      {/* Ink blot — slightly off-center, subtle */}
      <ellipse
        cx="170"
        cy="128"
        rx="14"
        ry="9"
        fill="rgb(var(--ink-primary))"
        opacity="0.25"
      />
      <ellipse cx="176" cy="126" rx="4" ry="2.5" fill="rgb(var(--ink-primary))" opacity="0.5" />
      {/* Diagonal "torn" indicator through the missing corner */}
      <path
        d="M 245 25 L 295 75"
        stroke="rgb(var(--line-primary))"
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity="0.5"
      />
    </svg>
  );
}
